import { Component, OnInit } from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";
import AppShareHelper from "src/app/shared/utils/helper";
import { PreviewAccessService } from "../../../services/preview-access.service";

type PreviewAccessStep = "email" | "code";

@Component({
  selector: "app-preview-access",
  templateUrl: "./preview-access.component.html",
  styleUrls: ["./preview-access.component.scss"],
})
export class PreviewAccessComponent implements OnInit {
  previewId = "";
  previewName = "digital-memory";
  step: PreviewAccessStep = "email";
  email = "";
  code = "";
  sending = false;
  verifying = false;
  error: string | null = null;
  info: string | null = null;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private previewAccessService: PreviewAccessService
  ) {}

  ngOnInit(): void {
    this.route.params.subscribe(params => {
      this.previewId = params["previewId"] || "";
      this.previewName = params["previewName"] || "digital-memory";

      if (
        this.previewId &&
        this.previewAccessService.isVerified(this.previewId) &&
        this.previewAccessService.getVerifiedDetail(this.previewId)
      ) {
        this.goToPreview();
      }
    });

    this.route.queryParams.subscribe(queryParams => {
      const emailFromQuery = queryParams && queryParams["email"];
      if (emailFromQuery && !this.email) {
        this.email = String(emailFromQuery).trim();
      }
    });
  }

  get canSendCode(): boolean {
    return this.isValidEmail(this.email) && !this.sending;
  }

  get canVerifyCode(): boolean {
    return !!(this.code || "").trim() && !this.verifying;
  }

  onSendCode(): void {
    if (!this.canSendCode || !this.previewId) {
      return;
    }

    const email = (this.email || "").trim().toLowerCase();
    this.sending = true;
    this.error = null;
    this.info = null;

    this.previewAccessService
      .sendAccessCode({
        email: email,
        digitalMemoryLogId: Number(this.previewId),
      })
      .subscribe(
        () => {
          this.sending = false;
          this.email = email;
          this.step = "code";
          this.info = "We sent a verification code to your email. Enter it below to continue.";
        },
        error => {
          this.sending = false;
          this.error = AppShareHelper.extractHttpErrorMessage(
            error,
            "Unable to send verification code. Please try again later."
          );
        }
      );
  }

  onVerifyCode(): void {
    if (!this.canVerifyCode || !this.previewId) {
      return;
    }

    const code = (this.code || "").trim();
    const email = (this.email || "").trim().toLowerCase();
    this.verifying = true;
    this.error = null;

    this.previewAccessService
      .verifyAccessCode({
        email: email,
        code: code,
        digitalMemoryLogId: Number(this.previewId),
      })
      .subscribe(
        res => {
          this.verifying = false;

          if (!this.previewAccessService.isVerifyValid(res)) {
            this.error = "Invalid or expired code. Please try again.";
            return;
          }

          const detail = this.previewAccessService.extractPreviewDetail(res);
          if (!detail) {
            this.error = "Verification succeeded but preview data was missing. Please try again.";
            return;
          }

          const token = (res && (res.accessToken || res.token)) || "";
          this.previewAccessService.markVerified(this.previewId, email, token, detail);
          this.goToPreview();
        },
        error => {
          this.verifying = false;
          this.error = AppShareHelper.extractHttpErrorMessage(
            error,
            "Invalid or expired code. Please try again."
          );
        }
      );
  }

  onChangeEmail(): void {
    this.step = "email";
    this.code = "";
    this.error = null;
    this.info = null;
  }

  onResendCode(): void {
    if (this.sending) {
      return;
    }
    this.onSendCode();
  }

  goBack(): void {
    if (!this.previewId) {
      this.router.navigate(["/"]);
      return;
    }
    this.router.navigate([`preview-${this.previewName}-${this.previewId}`]);
  }

  private goToPreview(): void {
    this.router.navigate([`preview-${this.previewName}-${this.previewId}`], {
      queryParams: { access: "verified" },
    });
  }

  private isValidEmail(value: string): boolean {
    const email = (value || "").trim();
    if (!email) {
      return false;
    }
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  }
}
